/*
  Wishlist migration shim — WP 5.1.1

  The published (Gecko) theme stored wishlists in localStorage under
  'mip_wishlist' as an array of numeric PRODUCT IDS. This theme (Ella)
  stores them under 'wishlistItem' as an array of product HANDLES.

  Because the two formats hold different identifiers, this is not a key
  rename: each ID has to be resolved to its handle. There is no public
  storefront endpoint that maps a product ID directly to a handle, so we
  page /products.json once and build the map client-side.

  Design constraints:
    - Runs at most once per browser (success or exhausted retries).
    - Never blocks rendering; all work is async and failure is silent.
    - Idempotent: merging is set-based, so re-runs cannot duplicate.
    - Degrades to a no-op. Worst case the customer sees the same empty
      wishlist they would have seen with no shim at all.
*/
(function () {
  'use strict';

  var LEGACY_KEY = 'mip_wishlist';
  var CURRENT_KEY = 'wishlistItem';
  var DONE_KEY = 'mip_wishlist_migrated';
  var ATTEMPT_KEY = 'mip_wishlist_migrate_attempts';

  var PAGE_SIZE = 250;
  var MAX_PAGES = 20; // 5,000 published products
  var MAX_ATTEMPTS = 3;

  function readJSON(key, fallback) {
    try {
      var raw = localStorage.getItem(key);
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function writeJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  }

  function finish() {
    try {
      localStorage.setItem(DONE_KEY, '1');
      localStorage.removeItem(ATTEMPT_KEY);
    } catch (e) {
      /* storage unavailable — nothing further to do */
    }
  }

  function bumpAttempts() {
    var n = 0;
    try {
      n = parseInt(localStorage.getItem(ATTEMPT_KEY) || '0', 10) || 0;
      localStorage.setItem(ATTEMPT_KEY, String(n + 1));
    } catch (e) {
      return MAX_ATTEMPTS; // storage broken: do not retry forever
    }
    return n + 1;
  }

  function migrate() {
    var done;
    try {
      done = localStorage.getItem(DONE_KEY);
    } catch (e) {
      return; // no localStorage at all (private mode, blocked cookies)
    }
    if (done) return;

    var legacy = readJSON(LEGACY_KEY, []);
    if (!legacy.length) {
      finish(); // nothing to carry over
      return;
    }

    if (bumpAttempts() > MAX_ATTEMPTS) {
      finish(); // give up quietly rather than fetch on every page view
      return;
    }

    var wanted = {};
    legacy.forEach(function (id) {
      wanted[String(id)] = true;
    });

    var found = {};
    var remaining = legacy.length;

    function fetchPage(page) {
      if (page > MAX_PAGES || remaining === 0) return apply();

      fetch('/products.json?limit=' + PAGE_SIZE + '&page=' + page, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var products = (data && data.products) || [];

          products.forEach(function (product) {
            var id = String(product.id);
            if (wanted[id] && !found[id]) {
              found[id] = product.handle;
              remaining--;
            }
          });

          if (products.length < PAGE_SIZE) return apply(); // last page
          fetchPage(page + 1);
        })
        .catch(function () {
          /*
            Endpoint unavailable or disabled. Leave the done flag unset so a
            later visit can retry, up to MAX_ATTEMPTS.
          */
        });
    }

    function apply() {
      var handles = [];
      Object.keys(found).forEach(function (id) {
        if (found[id]) handles.push(found[id]);
      });

      if (handles.length) {
        var current = readJSON(CURRENT_KEY, []);
        var merged = [];
        var seen = {};

        current.concat(handles).forEach(function (handle) {
          if (typeof handle === 'string' && handle && !seen[handle]) {
            seen[handle] = true;
            merged.push(handle);
          }
        });

        writeJSON(CURRENT_KEY, merged);
      }

      /*
        Marked done even when some IDs did not resolve. Unresolved IDs are
        products that are no longer published, and retrying will not find
        them.
      */
      finish();
    }

    fetchPage(1);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', migrate);
  } else {
    migrate();
  }
})();
